import { AlertTriangle, CheckCircle, FileText, Scale } from "lucide-react";
import Navbar from "../components/Navbar";
import Footer from "../components/Footer";
import ContactForm from "../components/ContactForm";

const cards = [
  {
    icon: FileText,
    color: "text-blue-600",
    title: "Use of Services",
    text:
      "By using our platform, you agree to comply with all applicable laws, website rules, payment requirements, and operational procedures published by Nexora Ventures.",
  },
  {
    icon: AlertTriangle,
    color: "text-amber-500",
    title: "Limitations",
    text:
      "We are not liable for indirect, incidental, special, or consequential damages, delays caused by third parties, network interruptions, or losses arising from misuse of the website or services.",
  },
  {
    icon: Scale,
    color: "text-indigo-600",
    title: "UAE Legal Alignment",
    text:
      "These terms are designed to support lawful website usage, payment transparency, and compliance with UAE legal and acquiring expectations.",
  },
  {
    icon: CheckCircle,
    color: "text-emerald-600",
    title: "Agreement",
    text:
      "Continued use of our platform confirms your acceptance of these terms, our privacy practices, and any additional policy statements published on this website.",
  },
];

const sections = [
  {
    title: "Acceptance of Terms",
    paragraphs: [
      "These Terms & Conditions govern your access to and use of this website and any related services offered by Nexora Ventures. By visiting the website, submitting an inquiry, requesting a quotation, purchasing a service, or otherwise engaging with us, you acknowledge that you have read, understood, and agreed to be bound by these Terms & Conditions.",
      "If you do not agree to these terms, you should discontinue use of the website and refrain from placing orders or submitting information through the platform.",
    ],
  },
  {
    title: "User Responsibilities",
    paragraphs: [
      "You must use this website only for lawful business and informational purposes. You agree not to introduce malicious code, attempt unauthorized access, interfere with website operations, impersonate another person or entity, or submit false, misleading, or incomplete information through contact forms, service requests, or payment-related interactions.",
      "You are responsible for ensuring that any contact details, billing information, project requirements, approvals, or supporting documents you provide are accurate, complete, current, and lawfully supplied. We are entitled to rely on the information you provide and are not responsible for delays, losses, or failed transactions caused by inaccurate or outdated information.",
    ],
  },
  {
    title: "Service Scope and Availability",
    paragraphs: [
      "All services are offered subject to availability, project suitability, internal review, and lawful feasibility. Any timelines, deliverables, consultations, proposals, or package examples displayed on the website are indicative unless confirmed in writing through an agreed quotation, proposal, or contract.",
      "We may modify, suspend, or discontinue any aspect of the website or service offering where reasonably necessary for maintenance, security, compliance, service quality, business continuity, or operational improvement.",
    ],
  },
  {
    title: "Sanctions and Restricted Countries",
    paragraphs: [
      "Nexora Ventures will NOT deal or provide any services or products to any OFAC (Office of Foreign Assets Control) sanctioned countries in accordance with the law of UAE.",
      "Users are strictly prohibited from using this website or our services for unlawful, deceptive, abusive, infringing, or fraudulent purposes, including sanctions evasion, money laundering, terrorist financing, payment abuse, attempted chargeback fraud, phishing, identity misuse, or any activity that violates UAE law or applicable international compliance requirements.",
    ],
  },
  {
    title: "Payment Terms",
    paragraphs: [
      "We accept payments online using Visa and MasterCard credit or debit cards in AED unless another currency arrangement is expressly confirmed in writing. Payment authorization is subject to the issuing bank, payment gateway review, fraud controls, and successful completion of any required verification checks.",
      "All charges, fees, and payment obligations will be presented to the customer before transaction confirmation. Payment may be required in full, in milestones, or under separate commercial terms depending on the nature of the service or project. Orders, service commencement, access credentials, or deliverables may be delayed or withheld until successful payment authorization and settlement are confirmed.",
    ],
  },
  {
    title: "Pricing and Taxes",
    paragraphs: [
      "All prices for services or products, where applicable, will be clearly displayed or otherwise communicated to the customer prior to payment confirmation. Unless expressly stated otherwise, prices are quoted in AED.",
      "Any applicable taxes, levies, government-imposed fees, bank charges, or regulatory charges will be identified where required by law or commercial practice. We reserve the right to revise prices, quotations, package structures, or promotional offers at any time before order confirmation.",
    ],
  },
  {
    title: "Cancellation Policy",
    paragraphs: [
      "Cancellation requests must be submitted as early as possible and may only be approved before substantial work has commenced, before provisioning has occurred, or before delivery obligations have been materially performed.",
      "Once a service has been initiated, resources allocated, or deliverables prepared, cancellation may result in partial charges, administrative deductions, or ineligibility for refund depending on the stage of performance and any non-recoverable costs already incurred.",
    ],
  },
  {
    title: "Refund Policy",
    paragraphs: [
      "Every refund request is reviewed individually, considering the type of product or service purchased, the amount of work already completed, the terms agreed with the customer, and any third-party expenses that cannot be recovered. A refund may be approved in cases such as an accidental duplicate payment, a service that Nexora Ventures is unable to provide due solely to circumstances within our control, or a cancellation accepted before significant work, resource assignment, or external expenditure has begun.",
      "Any refund that is approved will be returned exclusively through the same payment method used for the original transaction.",
      "The time required to process an approved refund may depend on the payment gateway, issuing bank, card network, compliance checks, and standard banking procedures. We will nevertheless make reasonable efforts to initiate it without undue delay following approval. Unless applicable law requires otherwise or a written agreement states differently, setup and consultation charges, strategy fees, finished milestones, priority work, delivered custom designs, advertising spend, platform fees, third-party software costs, and charges relating to completed work may not be refundable.",
    ],
  },
  {
    title: "Intellectual Property",
    paragraphs: [
      "All website content, including text, graphics, branding, logos, layouts, designs, source materials, downloadable content, software elements, and other intellectual property appearing on this website, is owned by or licensed to Nexora Ventures unless otherwise stated.",
      "No user may reproduce, republish, distribute, modify, reverse engineer, exploit, or create derivative works from website content without our prior written consent, except to the limited extent permitted by applicable law or clearly authorized under a separate written agreement.",
    ],
  },
  {
    title: "Limitation of Liability",
    paragraphs: [
      "The website, content, and services are provided on an as available and as applicable basis without warranties of uninterrupted availability, error-free operation, merchantability, fitness for a particular purpose, or non-infringement, except to the extent such disclaimers are not permitted by law.",
      "To the maximum extent permitted by applicable law, Nexora Ventures shall not be liable for any indirect, incidental, consequential, punitive, or special damages, including loss of profits, loss of opportunity, loss of data, reputational harm, business interruption, or payment-related losses caused by third-party processors, issuers, acquirers, telecommunications failures, or customer misuse.",
    ],
  },
  {
    title: "Dispute Resolution",
    paragraphs: [
      "If a dispute arises, the customer should first contact us using the communication details published on this website so that both parties may attempt a good-faith commercial resolution.",
      "If the matter cannot be resolved through direct communication, either party may pursue the matter through the competent courts or lawful authorities of the United Arab Emirates, subject to any mandatory legal requirements.",
    ],
  },
  {
    title: "Governing Law",
    paragraphs: [
      "Any purchase, dispute, or claim arising out of or in connection with this website shall be governed and construed in accordance with the laws of UAE.",
      "This governing law clause applies to contractual, non-contractual, statutory, and equitable claims to the fullest extent permitted by law.",
    ],
  },
  {
    title: "Termination Rights",
    paragraphs: [
      "We reserve the right to suspend, restrict, or terminate access to the website or our services, with or without notice where appropriate, if we reasonably believe that a user has violated these Terms & Conditions, engaged in fraud, created compliance risk, supplied false information, abused payment systems, or acted in a manner harmful to our business, systems, personnel, or lawful interests.",
      "Termination or suspension will not affect rights, obligations, payment liabilities, indemnities, intellectual property protections, or compliance-related provisions that by their nature are intended to survive such termination.",
    ],
  },
  {
    title: "Contact Information",
    paragraphs: [
      "For questions regarding these Terms & Conditions, payment matters, cancellations, or refunds, you may contact Nexora Ventures at business@nexorasventures.com or by phone at +971 0528 26 5243.",
      "Our operating location is Sharjah, Media City, UAE. We will make reasonable efforts to review legitimate requests within a commercially reasonable timeframe.",
    ],
  },
  {
    title: "Updates to Terms",
    paragraphs: [
      "We may update these Terms & Conditions at any time to reflect legal, regulatory, commercial, or operational changes. Revised terms will become effective upon posting on this website unless a different effective date is stated.",
      "Users are encouraged to review this page regularly. Continued use of the website after publication of updated terms constitutes acceptance of the revised Terms & Conditions.",
    ],
  },
];

const sectionId = (title) =>
  title.toLowerCase().replace(/&/g, "and").replace(/[^a-z0-9]+/g, "-").replace(/^-|-$/g, "");

function TermsConditions() {
  return (
    <div className="font-sans bg-gradient-to-b from-gray-50 to-white text-gray-900">
      <Navbar />

      <section className="bg-gradient-to-r from-blue-700 via-indigo-700 to-slate-900 py-20 text-white">
        <div className="mx-auto max-w-6xl px-6 text-center">
          <h1 className="mb-6 text-5xl font-extrabold">Terms & Conditions</h1>
          <p className="mx-auto max-w-2xl text-lg text-slate-200">
            Please read these terms carefully before using our services, placing an order, or making any payment through our website.
          </p>
        </div>
      </section>

      <section className="mx-auto max-w-6xl space-y-14 px-6 py-20">
        <div className="grid gap-8 md:grid-cols-2">
          {cards.map(({ icon: Icon, color, title, text }) => (
            <div key={title} className="rounded-3xl bg-white/70 p-8 shadow-lg backdrop-blur-lg">
              <Icon className={`mb-4 h-10 w-10 ${color}`} />
              <h2 className="mb-3 text-2xl font-semibold">{title}</h2>
              <p className="text-gray-600">{text}</p>
            </div>
          ))}
        </div>

        <div className="rounded-[2rem] border border-slate-200 bg-slate-900 px-8 py-8 text-white shadow-sm">
          <p className="text-xs uppercase tracking-[0.2em] text-slate-400">Compliance Clause</p>
          <p className="mt-3 text-lg font-semibold leading-8">
            Nexora Ventures will NOT deal or provide any services or products to any OFAC (Office of Foreign Assets Control) sanctioned countries in accordance with the law of UAE.
          </p>
        </div>

        <div className="space-y-8">
          {sections.map((section) => (
            <article
              key={section.title}
              id={sectionId(section.title)}
              className="rounded-3xl border border-gray-100 bg-white p-8 shadow-sm md:p-10"
            >
              <h2 className="mb-5 text-3xl font-bold text-slate-900">{section.title}</h2>
              <div className="space-y-4 leading-8 text-gray-700">
                {section.paragraphs.map((paragraph) => (
                  <p key={paragraph}>{paragraph}</p>
                ))}
              </div>
            </article>
          ))}
        </div>
      </section>

      <ContactForm />
      <Footer />
    </div>
  );
}

export default TermsConditions;
